import { useEffect, useState, useRef, useCallback } from 'react';
import {
  focusEditor,
  getPluginOptions,
  useComposedRef,
  useEditorRef,
  useHotkeys,
  ELEMENT_LINK,
  useVirtualFloatingLink,
  floatingLinkActions,
  floatingLinkSelectors,
  useFloatingLinkSelectors,
  triggerFloatingLinkInsert,
  setMarks,
} from '@udecode/plate';
import { useFocused } from 'slate-react';

import { useDetectClickOutside } from 'hooks/useDetectClickOutside';
import { useFloatingLinkEscape } from './useFloatingLinkEscape';
import { submitFloatingLink } from './submitFloatingLink';
import { getSelectionBoundingClientRect } from '../../../utils/getSelectionBoundingClientRect';
import { removeFakeSelection } from '../../../plugins/fake-selection/createFakeSelectionPlugin';

export const useFloatingLinkInsert = ({
  floatingOptions,
  ...props
}) => {
  const editor = useEditorRef();
  const focused = useFocused();
  const mode = useFloatingLinkSelectors().mode();
  const open = useFloatingLinkSelectors().isOpen(editor.id);

  const prevSelection = useRef(null);

  const { triggerFloatingLinkHotkeys } = getPluginOptions(
    editor,
    ELEMENT_LINK,
  );

  useHotkeys(
    triggerFloatingLinkHotkeys,
    (e) => {
      if (triggerFloatingLinkInsert(editor, { focused })) {
        e.preventDefault();
      }
    },
    {
      enableOnContentEditable: true,
    },
    [focused],
  );

  const onTriggered = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (floatingLinkSelectors.mode() === 'insert') {

      if (prevSelection.current) {
        focusEditor(editor, prevSelection.current);
      }
      // Set arbitrary timeout because sometimes focus doesn't update before submit.
      setTimeout(() => {
        submitFloatingLink(editor);
        floatingLinkActions.hide();
      }, 0);
    }
  }, []);

  const ref = useDetectClickOutside({
    onTriggered,
  });

  const { update, style, refs } = useVirtualFloatingLink({
    editorId: editor.id,
    open: open && mode === 'insert',
    getBoundingClientRect: getSelectionBoundingClientRect,
    /* #TODO: This causes link application to fail, so need to remove this for now.
    whileElementsMounted: () => {
      // Apply fake highlighting while element is mounted.
      setMarks(editor, { fakeSelection: true });
      return () => removeFakeSelection(editor);
    },
    */
    ...floatingOptions,
  });

  // wait for update before focusing input
  useEffect(() => {
    if (open) {
      prevSelection.current = editor?.selection;
      update();
      floatingLinkActions.updated(true);
    } else {
      floatingLinkActions.updated(false);
    }
  }, [open, update]);

  useFloatingLinkEscape();

  return {
    style: {
      ...style,
      zIndex: 42,
    },
    ...props,
    ref: useComposedRef(props.ref, refs.setFloating, ref),
  };
};
